import { Box, Typography } from '@mui/material';
import { FormikContextType, getIn } from 'formik';
import { useEffect, useState } from 'react';
import { CheckboxComponent } from '../../inputComponents/CheckboxComponent';
import TooltipText from '../../inputComponents/TooltipText';
import { RadioButton } from '../../inputComponents/RadioButton';
import { AdditionalDetailsMechanicSpecifics } from './AdditionalDetailsMechanicSpecifics';
import './AdditionalPromoDetails.css';
import CaptchaCheckerComponent from './CaptchaChecker.component';
import { ICreatePromotionState } from '../../../types/componentTypes/assignPromo';
import { PromoMechanic } from '../../../types/componentTypes/editPromotion';

interface AdditionalPromoDetailsProps {
    formik: FormikContextType<ICreatePromotionState>;
}

interface labelsMapType {
    [key: string]: {
        [key: string]: {
            [key: string]: string;
        };
    };
}

function AdditionalPromoDetails({ formik }: AdditionalPromoDetailsProps) {
    const [mechanic, setMechanic] = useState(formik.values.tempParams.flowOption);

    useEffect(() => {
        setMechanic(formik.values.tempParams.flowOption);
    }, [formik.values.tempParams.flowOption]);

    const mechanicSpecificLabels = (mechanic: string, field: string, subfield: string) => {
        const labelsMap: labelsMapType = {
            "Lucky Numbers": {
                queryWallet: {
                    label: 'Lucky Numbers History (Optional)',
                    tooltip:
                        'Once enabled, participants will be able to see their lucky numbers history',
            }
            }
        };
        return labelsMap[mechanic]?.[field]?.[subfield] || '';
    }

    const flowsMap = {
        addTransaction: { flowLambdas: ['transactionLambda'] },
        autoRedeemCnG: { flowLambdas: ['autoRedeemPrizeLambda'] },
        listPrizes: {
            flowLambdas: ['prizeQueryLambda'],
            params: {
                priorityOrder: formik.values.priorityOrder,
                additionalInformation: true,
            },
        },
        queryVouchers: { flowLambdas: ['digitalCodesQueryByUserLambda'] },
        queryWallet: { flowLambdas: ['walletLambda'] },
        listTransactions: {flowLambdas: ["listTransactionsByUserLambda"]},
        redeemPrize: {
            checkerLambdas: ['prizeCheckerLambda', 'currencyCheckerLambda'],
            flowLambdas: ['prizeRedeemLambda'],
        },
        redeemVoucher: { flowLambdas: ['digitalCodesRedeemLambda'] },
        additionalInformation: {
            flowLambdas: ['additionalConfigurationInformationLambda']
        }
    };

    const addFlow = (e: any) => {
        const { checked, name } = e.target;
        const flow = name.split('.')[1];
        if (checked) {
            formik.setFieldValue('additionalDetails', {
                ...formik.values.additionalDetails,
                [flow]: flowsMap[flow as keyof typeof flowsMap],
            });
        } else {
            const additionalDetails = getIn(formik.values, 'additionalDetails');
            delete additionalDetails[flow];
            formik.setFieldValue('additionalDetails', additionalDetails);
        }
    };

    const handlePriorityParam = () => {
        formik.setFieldValue(
            'additionalDetails.listPrizes.params.priorityOrder',
            formik.values.priorityOrder === 'ASC' ? 'DESC' : 'ASC'
        );
    };

    const handleImagePromotionEntry = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { checked } = e.target;
        if (checked) {
            formik.setFieldValue('params.imageEntry', true);
        } else {
            formik.setFieldValue('params.imageEntry', false);
        }
    };

    return (
        <Box className='promo-details'>
            <Typography variant='h4'>Additional Details</Typography>
            <Typography className='typography-style' variant='body1'>
                Select functionalities required for this promotion
            </Typography>
            <Box className='promo-options'>
                {mechanic !== PromoMechanic.PromoEntry  && (
                    <>
                    {mechanic !== PromoMechanic.LuckyNumbers && (
                        <CheckboxComponent
                            customBoxClass='voucher-checkbox'
                            fieldName={'tempParams.listPrizes'}
                            label={
                                <TooltipText
                                    dataTestId='prize-catalog-tooltip'
                                    styleName='details-checkbox-tooltip'
                                    text='Prize Catalog (Optional)'
                                    textVariant='body1'
                                    tooltip={
                                        'Once enabled, prize catalog can be shown to participants'
                                    }
                                />
                            }
                            customAction={addFlow}
                        />
                    )}
                        {formik.values.tempParams.listPrizes && (
                            <Box>
                                <Typography variant='body1'>Prize List Priority</Typography>
                                <RadioButton
                                    formik={formik}
                                    customAction={handlePriorityParam}
                                    values={[
                                        { value: 'ASC', label: 'Ascending' },
                                        { value: 'DESC', label: 'Descending' },
                                    ]}
                                    radioGroupName={'priorityOrder'}
                                />
                            </Box>
                        )}
                        <CheckboxComponent
                            customBoxClass='voucher-checkbox'
                            fieldName={'tempParams.queryVouchers'}
                            label={
                                <TooltipText
                                    dataTestId='vouchers-history-tooltip'
                                    styleName='details-checkbox-tooltip'
                                    text={
                                        mechanic === PromoMechanic.LuckyNumbers ?
                                        mechanicSpecificLabels(mechanic, 'queryWallet', 'label') :
                                        'Vouchers History (Optional)'
                                    }
                                    textVariant='body1'
                                    tooltip={
                                        mechanic === PromoMechanic.LuckyNumbers ?
                                        mechanicSpecificLabels(mechanic, 'queryWallet', 'tooltip') :
                                        'Once enabled, vouchers history can be shown to participants'
                                    }
                                />
                            }
                            customAction={addFlow}
                        />
                        <AdditionalDetailsMechanicSpecifics
                            formik={formik}
                            mechanic={mechanic}
                            customActions={[addFlow]}
                        />
                    </>
                )}

                <CaptchaCheckerComponent formik={formik} />
                {mechanic !== PromoMechanic.LuckyNumbers && (
                    <CheckboxComponent
                    customBoxClass="wallet-checkbox"
                    fieldName={"tempParams.queryWallet"}
                    label={
                        <TooltipText
                            styleName="details-checkbox-tooltip"
                            text="Wallet (Optional)"
                            dataTestId="wallet-tooltip"
                            textVariant="body1"
                            tooltip={
                                "Once enabled, participants will be able to see their wallets"
                            }
                        />
                    }
                    customAction={addFlow}
                />
                )}

                {mechanic === PromoMechanic.PromoEntry && (
                    <CheckboxComponent
                        customBoxClass="wallet-checkbox"
                        fieldName={"tempParams.additionalInformation"}
                        label={
                            <TooltipText
                                styleName="details-checkbox-tooltip"
                                text="List Additional Information (Optional)"
                                dataTestId="list-additional-info-tooltip"
                                textVariant="body1"
                                tooltip={
                                    "This is a legacy option where you can list all the custom additional information."
                                }
                            />
                        }
                        customAction={addFlow}
                    />
                )}

                {[PromoMechanic.InstantWin, PromoMechanic.PromoEntry]
                    .includes(mechanic as PromoMechanic) && (
                    //! Temp fix just to fix the bug in release-123 and will be refactored in BUG 710914
                    <CheckboxComponent
                        fieldName={'params.imageEntry'}
                        label={
                            <TooltipText
                                styleName='image-promotion-checkbox-tooltip'
                                text='Image Promotion Entry'
                                dataTestId="image-promo-entry-tooltip"
                                textVariant='body1'
                                tooltip={
                                    'The Image Promo Entry option will enable an image upload as a promotion entry'
                                }
                            />
                        }
                        customBoxClass='imagePromoEntryCheckbox'
                        customAction={handleImagePromotionEntry}
                    />
                )}
            </Box>
        </Box>
    );
}

export { AdditionalPromoDetails };
